import React, { memo, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import GenericFilter from '../../Generics/GenericFilter';
import GenericSlider from '../../Generics/GenericSlider';

function UnwindingFilter({ onFilter, onFilterSlider, onClear, filterNames, options }) {
  const [operationTimeByMonth, setOperationTimeByMonth] = useState({ defaultValue: { min: 0, max: 0 }, newValue: { min: 0, max: 0 } });
  const [currentProfitPercentage, setCurrentProfitPercentage] = useState({ defaultValue: { min: 0, max: 0 }, newValue: { min: 0, max: 0 } });
  const [currentProfitOnMaxContractedPercentage, setCurrentProfitOnMaxContractedPercentage] = useState({ defaultValue: { min: 0, max: 0 }, newValue: { min: 0, max: 0 } });
  const [filters, setFilters] = useState([
    {
      name: 'advisorCodes',
      title: 'Assessores',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'clients',
      title: 'Clientes',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'tickers',
      title: 'Ativos',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
    {
      name: 'structures',
      title: 'Estruturas',
      placeholder: ' ',
      type: 'default',
      options: [],
    },
  ]);

  const getOptionSelected = useCallback(option => option?.map(({ id, isSelected }) => (isSelected ? id : undefined))?.filter(data => !!data) || [], []);

  const createOptions = useCallback(optionData => optionData?.map(option => ({
    id: `${option}`,
    value: option,
    label: `${option}`,
    isSelected: false,
  })), []);

  const createFilter = useCallback(() => {
    const ADVISORS = filters[0];
    const CLIENTS = filters[1];
    const TICKERS = filters[2];
    const STRUCTURES = filters[3];

    const HAS_ADVISORS_OPTIONS = ADVISORS.options.length === 0;
    const HAS_CLIENTS_OPTIONS = CLIENTS.options.length === 0;
    const HAS_TICKERS_OPTIONS = TICKERS.options.length === 0;
    const HAS_STRUCTURES_OPTIONS = STRUCTURES.options.length === 0;
    if (HAS_ADVISORS_OPTIONS || HAS_CLIENTS_OPTIONS || HAS_TICKERS_OPTIONS || HAS_STRUCTURES_OPTIONS) {
      setFilters([
        { ...ADVISORS, options: createOptions(options?.AdvisorCodes) },
        { ...CLIENTS, options: createOptions(options?.Clients) },
        { ...TICKERS, options: createOptions(options?.Tickers) },
        { ...STRUCTURES, options: createOptions(options?.Structures) },
      ]);
    }
  }, [filters, options]);

  const createSlider = useCallback(() => {
    const OPERATION_TIME_BY_MONTH = {
      min: options?.OperationTimeByMonth?.min,
      max: options?.OperationTimeByMonth?.max,
    };

    const CURRENT_PROFIT_PERCENTAGE = {
      min: options?.CurrentProfitPercentage?.min,
      max: options?.CurrentProfitPercentage?.max,
    };

    const CURRENT_PROFIT_ONMAXCONTRACTED_PERCENTAGE = {
      min: options?.CurrentProfitOnMaxContractedPercentage?.min,
      max: options?.CurrentProfitOnMaxContractedPercentage?.max,
    };

    setOperationTimeByMonth({ defaultValue: OPERATION_TIME_BY_MONTH, newValue: OPERATION_TIME_BY_MONTH });
    setCurrentProfitPercentage({ defaultValue: CURRENT_PROFIT_PERCENTAGE, newValue: CURRENT_PROFIT_PERCENTAGE });
    setCurrentProfitOnMaxContractedPercentage({ defaultValue: CURRENT_PROFIT_ONMAXCONTRACTED_PERCENTAGE, newValue: CURRENT_PROFIT_ONMAXCONTRACTED_PERCENTAGE });

  }, [options]);

  const handleOnFilter = useCallback((filterData) => {
    onFilter({
      AdvisorCodes: getOptionSelected(filterData[0].options),
      Clients: getOptionSelected(filterData[1].options),
      Tickers: getOptionSelected(filterData[2].options),
      Structures: getOptionSelected(filterData[3].options),
    });
  }, []);

  const handleOnFilterSlider = useCallback((sliderData, sliderId) => {
    const MIN = sliderData[0];
    const MAX = sliderData[1];
    const NEW_DATA = { min: MIN, max: MAX };

    if(sliderId === 'operationTimeByMonth')
    {
      setOperationTimeByMonth(oldState => ({ ...oldState, newValue: NEW_DATA }));
      onFilterSlider({
        ...(({ OperationTimeByMonth: { ...NEW_DATA } })),
      });
    }

    if(sliderId === 'currentProfitPercentage')
    {
      setCurrentProfitPercentage(oldState => ({ ...oldState, newValue: NEW_DATA }));
      onFilterSlider({
        ...(({ CurrentProfitPercentage: { ...NEW_DATA } })),
      });
    }

    if(sliderId === 'currentProfitOnMaxContractedPercentage')
    {
      setCurrentProfitOnMaxContractedPercentage(oldState => ({ ...oldState, newValue: NEW_DATA }));
      onFilterSlider({
        ...(({ CurrentProfitOnMaxContractedPercentage: { ...NEW_DATA } })),
      });
    }

  }, []);

  const handleOnClear = useCallback(() => {
    const ADVISORS = filters[0];
    const CLIENTS = filters[1];
    const TICKERS = filters[2];
    const STRUCTURES = filters[3];

    setFilters([
      { ...ADVISORS, options: createOptions(options?.AdvisorCodes) },
      { ...CLIENTS, options: createOptions(options?.Clients) },
      { ...TICKERS, options: createOptions(options?.Tickers) },
      { ...STRUCTURES, options: createOptions(options?.Structures) },
    ]);

    onClear({
      AdvisorCodes: [],
      Clients: [],
      Tickers: [],
      Structures: [],
    });
  }, [options]);

  useEffect(() => {
    createFilter();
    createSlider();
  }, [options]);

  return (
    <GenericFilter
      onFilter={handleOnFilter}
      onClear={handleOnClear}
      filters={filters}
      filterNames={filterNames}
    >
      <GenericSlider
        onChange={sliderData => handleOnFilterSlider(sliderData, 'operationTimeByMonth')}
        title="Tempo na Operação (meses)"
        minDistance={1}
        min={operationTimeByMonth.defaultValue.min}
        max={operationTimeByMonth.defaultValue.max}
        defaultValue={[operationTimeByMonth.newValue.min, operationTimeByMonth.newValue.max]}
        isDisabled={options?.OperationTimeByMonth?.min === 0 && options?.OperationTimeByMonth?.max === 0}
      />

      <GenericSlider
        onChange={sliderData => handleOnFilterSlider(sliderData, 'currentProfitPercentage')}
        title="Resultado Bruto Atual (%)"
        minDistance={1}
        min={currentProfitPercentage.defaultValue.min}
        max={currentProfitPercentage.defaultValue.max}
        defaultValue={[currentProfitPercentage.newValue.min, currentProfitPercentage.newValue.max]}
        isDisabled={options?.CurrentProfitPercentage?.min === 0 && options?.CurrentProfitPercentage?.max === 0}
      />

      <GenericSlider
        onChange={sliderData => handleOnFilterSlider(sliderData, 'currentProfitOnMaxContractedPercentage')}
        title="Ganho Atual/Máximo (%)"
        minDistance={1}
        min={currentProfitOnMaxContractedPercentage.defaultValue.min}
        max={currentProfitOnMaxContractedPercentage.defaultValue.max}
        defaultValue={[currentProfitOnMaxContractedPercentage.newValue.min, currentProfitOnMaxContractedPercentage.newValue.max]}
        isDisabled={options?.CurrentProfitOnMaxContractedPercentage?.min === 0 && options?.CurrentProfitOnMaxContractedPercentage?.max === 0}
      />
    </GenericFilter>
  );
}

UnwindingFilter.propTypes = {
  onFilter: PropTypes.func.isRequired,
  onFilterSlider: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  filterNames: PropTypes.arrayOf(PropTypes.string.isRequired),
  options: PropTypes.shape({
    AdvisorCodes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
    Clients: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number]).isRequired).isRequired,
    Tickers: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
    Structures: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string.isRequired]).isRequired).isRequired,
    OperationTimeByMonth: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
    }).isRequired,
    CurrentProfitPercentage: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
    }).isRequired,
    CurrentProfitOnMaxContractedPercentage: PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};

export default memo(UnwindingFilter);
